import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { resolveBrandingLogoUrl } from '../../../utils/brandingUrl';
import type { ReactNode } from 'react';

type Filiere = {
  id: string;
  code?: string;
  libelle?: string;
  description?: string | null;
  urlImage?: string | null;
  domaine?: string;
  ordre?: number;
  estActive?: boolean;
  _count?: { cohortes?: number; sessions?: number };
};

type Props = {
  filiere: Filiere;
  domaineLabel: string;
  onEdit: () => void;
  deleteAction?: ReactNode;
};

function MetaChip({ label, value, mono }: { label: string; value: string | number; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-outline-variant/15 bg-white px-3 py-2 shadow-sm">
      <p className="text-[10px] font-bold uppercase tracking-wide text-on-surface-variant/80">{label}</p>
      <p className={`mt-0.5 text-sm font-semibold text-slate-800 ${mono ? 'break-all font-mono text-xs' : ''}`}>
        {value}
      </p>
    </div>
  );
}

export default function FiliereCatalogListItem({ filiere, domaineLabel, onEdit, deleteAction }: Props) {
  const cover = resolveBrandingLogoUrl(filiere.urlImage);
  const isActive = filiere.estActive !== false;

  return (
    <article className="group flex min-w-0 flex-col gap-4 rounded-2xl border border-outline-variant/15 bg-white p-4 shadow-sm transition hover:border-primary/20 hover:shadow-md sm:flex-row sm:items-stretch sm:p-5">
      <div className="relative shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-primary/10 to-slate-100 sm:w-36">
        {cover ? (
          <img src={cover} alt="" className="h-28 w-full object-cover sm:h-full sm:min-h-[7.5rem]" />
        ) : (
          <div className="flex h-28 w-full items-center justify-center sm:h-full sm:min-h-[7.5rem]">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-md">
              <LucideSymbolIcon name="menu_book" tone="primary" size={28} />
            </span>
          </div>
        )}
        <span
          className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
            isActive ? 'bg-emerald-500 text-white' : 'bg-slate-600 text-white'
          }`}
        >
          {isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-black leading-snug text-slate-900">{filiere.libelle || '—'}</h3>
            <p className="mt-1 text-xs font-semibold text-primary">{domaineLabel}</p>
            {filiere.description?.trim() ? (
              <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-on-surface-variant">
                {filiere.description}
              </p>
            ) : (
              <p className="mt-2 text-sm italic text-on-surface-variant/70">
                Aucune description pour la vitrine publique.
              </p>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-primary/20 bg-primary/5 px-3.5 py-2 text-xs font-bold text-primary opacity-90 transition group-hover:opacity-100 hover:bg-primary/10"
            >
              <LucideSymbolIcon name="edit" tone="primary" size={16} />
              Modifier
            </button>
            {deleteAction}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
          <MetaChip label="Code" value={filiere.code ?? '—'} mono />
          <MetaChip label="Domaine" value={domaineLabel} />
          <MetaChip label="Cohortes" value={filiere._count?.cohortes ?? 0} />
          <MetaChip label="Sessions" value={filiere._count?.sessions ?? 0} />
          <MetaChip label="Ordre" value={filiere.ordre ?? 0} />
        </div>
      </div>
    </article>
  );
}
